<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
import MarkdownIt from "markdown-it";
import { useRoute } from "vue-router";
import SharedCompliantForm from "~/components/Shared/CompliantForm.vue";
import ConfirmationModal from "@/components/Shared/ConfirmationModal.vue";

interface TranslatedDocument {
  id?: number;
  languageLabel: string;
  languageCode: string;
  textDirection?: "ltr" | "rtl";
  bodyContent?: string | null;
  pdf?: { url?: string; name?: string } | null;
}

const props = defineProps<{
  content: {
    heading?: string;
    privacyNote?: string;
    emergencyCopy?: string;
    emergencyPhone?: string;
    exitCopy?: string;
    quickExitLabel?: string;
    quickExitUrl?: string;
    documents?: TranslatedDocument[];
  };
}>();

const DEFAULTS = {
  exitUrl: "https://www.google.com.au",
  exitLabel: "EXIT THIS SITE NOW",
  emergencyCopy: "Are you in immediate danger or need help right now? Call 000.",
  emergencyPhone: "000",
  exitCopy: 'Need to exit this page quickly? Click the "QUICK EXIT" button.',
  privacyNote:
    "Use the Quick Exit button to leave this page quickly. For additional privacy, close your browser and clear your browsing history.",
};

const exitUrl = computed(() => props.content.quickExitUrl || DEFAULTS.exitUrl);
const exitLabel = computed(
  () => props.content.quickExitLabel || DEFAULTS.exitLabel
);
const exitCopy = computed(() => props.content.exitCopy || DEFAULTS.exitCopy);
const emergencyPhone = computed(
  () => props.content.emergencyPhone || DEFAULTS.emergencyPhone
);
const privacyNote = computed(
  () => props.content.privacyNote || DEFAULTS.privacyNote
);

// The emergency line renders as bold copy with the phone number as a tel: link,
// so it is tappable on mobile. The copy is split around the number rather than
// hard-coded so the wording stays editable in the CMS.
const emergencyParts = computed(() => {
  const copy = props.content.emergencyCopy || DEFAULTS.emergencyCopy;
  const phone = emergencyPhone.value;
  const at = copy.indexOf(phone);
  if (at === -1) return { before: copy, after: "" };
  return { before: copy.slice(0, at), after: copy.slice(at + phone.length) };
});

// English carries body content and no PDF; every other language carries a PDF.
// An entry with neither is unusable, so it is dropped rather than rendered as
// an empty tab.
const documents = computed<TranslatedDocument[]>(() =>
  (props.content.documents || []).filter(
    (doc) => doc?.pdf?.url || doc?.bodyContent
  )
);

// English is always authored first and is the default selection
const selectedIndex = ref(0);
const selectedDocument = computed<TranslatedDocument | undefined>(
  () => documents.value[selectedIndex.value] ?? documents.value[0]
);

const showsBody = computed(() => Boolean(selectedDocument.value?.bodyContent));

/**
 * IGNITE-65: bodyContent is a Strapi richtext field, which holds Markdown. It
 * was being passed straight to v-html, so authored links and emphasis rendered
 * as literal [label](url) and **text**. Same MarkdownIt options the other
 * Strapi blocks use (SimpleText, CisDocuments), so authoring behaves
 * consistently across the site.
 */
const md = new MarkdownIt({
  html: true, // Enable HTML tags for phone number links
  linkify: true, // Enable auto-converting URL-like text to links
  breaks: true, // Convert \n to <br>
});

/**
 * IGNITE-65: the English body used to live in a SimpleText block, which also
 * substitutes the ###FORM### placeholder with the complaint form. Moving the
 * copy in here left the placeholder printing as literal text, so the same
 * split is done on this side. The page checks match SimpleText's so the form
 * behaves identically on both pages.
 */
const FORM_PLACEHOLDER = "###FORM###";
const route = useRoute();
const showModal = ref(false);

const hasForm = computed(() =>
  Boolean(selectedDocument.value?.bodyContent?.includes(FORM_PLACEHOLDER))
);

const isFinancialHardshipPage = computed(() =>
  route.path.includes("legals-financial-hardship")
);
const isDomesticAbusePage = computed(() =>
  route.path.includes("domestic-and-family-abuse")
);

const bodyParts = computed(() => {
  const body = selectedDocument.value?.bodyContent;
  if (!body) return { before: "", after: "" };

  const [before, after = ""] = body.split(FORM_PLACEHOLDER);
  return { before: md.render(before), after: md.render(after) };
});

const bodyHtml = computed(() =>
  selectedDocument.value?.bodyContent
    ? md.render(selectedDocument.value.bodyContent)
    : ""
);

// PRIVACY: PDFs must always be served same-origin (via the Nuxt uploads
// proxy), never from the CMS domain or an external viewer
const toFirstPartyUrl = (url: string) => {
  const path = url.replace(/^https?:\/\/[^/]+/, "");
  return path.startsWith("/uploads/") ? `/api${path}` : path;
};

const selectedPdfUrl = computed(() => {
  const url = selectedDocument.value?.pdf?.url;
  return url ? toFirstPartyUrl(url) : "";
});

const viewerTitle = computed(() =>
  selectedDocument.value
    ? `${selectedDocument.value.languageLabel} support document`
    : "Support document"
);

// Screen-reader announcement on language change (aria-live status region)
const statusMessage = ref("");
const selectDocument = (index: number) => {
  selectedIndex.value = index;
  const doc = documents.value[index];
  if (!doc) return;
  statusMessage.value = doc.bodyContent
    ? `${doc.languageLabel} selected. Showing page content.`
    : `${doc.languageLabel} document selected and loading.`;
};

// Language carousel arrows. Shown always (as on the reference page) and
// disabled when the list is not scrollable or already at an end.
const listRef = ref<HTMLElement | null>(null);
const canScrollPrev = ref(false);
const canScrollNext = ref(false);

const updateScrollState = () => {
  const el = listRef.value;
  if (!el) return;
  const max = el.scrollWidth - el.clientWidth;
  canScrollPrev.value = el.scrollLeft > 1;
  canScrollNext.value = max > 1 && el.scrollLeft < max - 1;
};

const scrollLanguages = (direction: 1 | -1) => {
  const el = listRef.value;
  if (!el) return;
  // Scroll by most of a viewport width so items are not left half-cut
  el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: "smooth" });
};

/**
 * IGNITE-65: no mobile browser renders a multi-page PDF inline in an iframe.
 * iOS Safari shows a broken frame; Android Chrome replaces it with its own
 * grey card carrying the raw filename and an Open button, which looks like a
 * fault and leaks the file name. Both get our own fallback instead, which
 * names the document properly.
 *
 * Detected from the user agent rather than a width query on purpose: this is
 * about what the browser can render, not how much room it has.
 */
const cannotEmbedPdf = computed(() => {
  if (!import.meta.client) return false;

  const ua = navigator.userAgent;
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);

  return isIOS || isAndroid;
});

// Open a neutral site in a new tab AND replace the current page so the
// browser back button does not return here (privacy requirement)
const quickExit = () => {
  window.open(exitUrl.value, "_blank", "noopener,noreferrer");
  window.location.replace(exitUrl.value);
};

/* ---------------------------------------------------------------------------
 * Sticky Quick Exit bar
 *
 * The bar has to stay under the site header for the WHOLE page, not just while
 * this block is on screen, so CSS sticky is not an option: sticky releases once
 * its containing block scrolls past, and .page-container (the real scroller,
 * overflow-y:auto) hijacks the sticky context anyway. A fixed bar teleported to
 * body sidesteps both, and a spacer inside the block reserves its height so the
 * bar never covers content.
 * ------------------------------------------------------------------------- */
const barRef = ref<HTMLElement | null>(null);
const headerHeight = ref(80); // OnePass desktop header is 80px; measured live
const barHeight = ref(0);

// Desktop (.onepass-header, 80px) and mobile (.mobile-header) headers differ,
// so the offset is measured rather than assumed.
const measureHeaderHeight = () => {
  for (const selector of [".onepass-header", ".mobile-header", "header"]) {
    const el = document.querySelector(selector) as HTMLElement | null;
    const height = el ? Math.round(el.getBoundingClientRect().height) : 0;
    if (height > 0) return height;
  }
  return 80;
};

const measure = () => {
  const header = measureHeaderHeight();
  if (headerHeight.value !== header) headerHeight.value = header;

  const height = barRef.value
    ? Math.round(barRef.value.getBoundingClientRect().height)
    : 0;
  if (height > 0 && barHeight.value !== height) barHeight.value = height;
};

let onResize: (() => void) | null = null;

onMounted(async () => {
  await nextTick();
  measure();
  updateScrollState();
  onResize = () => {
    measure();
    updateScrollState();
  };
  window.addEventListener("resize", onResize, { passive: true });
});

onUnmounted(() => {
  if (onResize) window.removeEventListener("resize", onResize);
});
</script>

<template>
  <section v-if="documents.length" class="translated-documents">
    <!-- Fixed Quick Exit bar, pinned directly under the site header.
         Rendered in place rather than teleported to body: nothing in the
         ancestor chain establishes a containing block for fixed elements
         (.page-container is only position:relative with overflow, neither of
         which affects fixed), so the teleport bought nothing and cost a leak -
         its nodes stayed behind in body after a client-side route change. -->
      <!-- The site header is an inset rounded bar, so the strip either side of it
           is transparent and page content scrolls through the gaps. This backdrop
           fills that strip with the page background, behind the header itself.
           Scoped to these pages rather than changing the shared header. -->
      <div
        class="header-backdrop"
        :style="{ height: `${headerHeight}px` }"
        aria-hidden="true"
      />

      <div
        ref="barRef"
        class="quick-exit-bar"
        :style="{ top: `${headerHeight}px` }"
        role="region"
        aria-label="Emergency help and quick exit"
      >
        <!-- IGNITE-65: the bar is fixed, so it sits outside the page's own
             .container. This wrapper reproduces that container so the bar copy
             lines up with the page content instead of hugging the viewport. -->
        <div class="quick-exit-container">
          <div class="quick-exit-inner">
            <p class="quick-exit-copy">
              <b>
                {{ emergencyParts.before
                }}<a :href="`tel:${emergencyPhone}`" class="quick-exit-phone">{{
                  emergencyPhone
                }}</a
                >{{ emergencyParts.after }}
              </b>
              <br />
              {{ exitCopy }}
            </p>

            <button
              type="button"
              class="quick-exit-button"
              :aria-label="`${exitLabel}. Leaves this page and opens Google.`"
              @click="quickExit"
            >
              {{ exitLabel }}
            </button>
          </div>
        </div>
      </div>

    <!-- Reserves the fixed bar's height so it never covers page content -->
    <div class="quick-exit-spacer" :style="{ height: `${barHeight}px` }" />

    <div class="translated-documents-inner">
      <!-- mt-0 guards against margin-top leaking from CMS page-level <style> h2 rules -->
      <h1
        v-if="content.heading"
        class="text-primary text-3xl font-bold mt-0 mb-4"
      >
        {{ content.heading }}
      </h1>

      <p class="text-font-primary mb-6">
        <strong>Privacy note:</strong> {{ privacyNote }}
      </p>

      <!-- Language carousel -->
      <div class="language-carousel">
        <button
          type="button"
          class="carousel-arrow"
          :disabled="!canScrollPrev"
          aria-label="Show previous languages"
          @click="scrollLanguages(-1)"
        >
          <svg width="10" height="18" viewBox="0 0 10 18" fill="none" aria-hidden="true">
            <path
              d="M9 1L1 9L9 17"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>

        <ul ref="listRef" class="language-list" role="list" @scroll="updateScrollState">
          <li v-for="(doc, index) in documents" :key="doc.id ?? doc.languageCode">
            <button
              type="button"
              class="language-link"
              :class="{ 'language-link--active': index === selectedIndex }"
              :lang="doc.languageCode"
              :aria-current="index === selectedIndex ? 'true' : undefined"
              @click="selectDocument(index)"
            >
              {{ doc.languageLabel }}
            </button>
          </li>
        </ul>

        <button
          type="button"
          class="carousel-arrow"
          :disabled="!canScrollNext"
          aria-label="Show more languages"
          @click="scrollLanguages(1)"
        >
          <svg width="10" height="18" viewBox="0 0 10 18" fill="none" aria-hidden="true">
            <path
              d="M1 1L9 9L1 17"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>

      <div role="status" aria-live="polite" aria-atomic="true" class="sr-only">
        {{ statusMessage }}
      </div>

      <!-- English: the page body content, no PDF container -->
      <div
        v-if="showsBody && hasForm"
        class="document-body"
        :lang="selectedDocument?.languageCode"
        :dir="selectedDocument?.textDirection || 'ltr'"
      >
        <div v-html="bodyParts.before" />
        <SharedCompliantForm
          :hideHardshipLine="isFinancialHardshipPage"
          :isDomesticAbuse="isDomesticAbusePage"
          @submitted="showModal = true"
        />
        <ConfirmationModal v-if="showModal" @close="showModal = false" />
        <div v-html="bodyParts.after" />
      </div>

      <div
        v-else-if="showsBody"
        class="document-body"
        :lang="selectedDocument?.languageCode"
        :dir="selectedDocument?.textDirection || 'ltr'"
        v-html="bodyHtml"
      />

      <!-- Any other language: the PDF container, body content hidden -->
      <div
        v-else-if="selectedPdfUrl"
        class="viewer-container"
        :lang="selectedDocument?.languageCode"
        :dir="selectedDocument?.textDirection || 'ltr'"
      >
        <template v-if="!cannotEmbedPdf">
          <iframe
            :src="selectedPdfUrl"
            :title="viewerTitle"
            class="viewer-frame"
          />
          <!-- IGNITE-65: the embedded viewer gives no reliable way to save the
               file, and browser PDF toolbars are inconsistent, so the download
               is offered explicitly. iOS already gets its own button below. -->
          <div class="viewer-footer">
            <a
              :href="selectedPdfUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="viewer-download-link"
              :aria-label="`Open or download ${viewerTitle} in a new tab`"
            >
              Open / download this document (PDF)
            </a>
          </div>
        </template>
        <div v-else class="viewer-ios-fallback">
          <p>{{ viewerTitle }}</p>
          <a
            :href="selectedPdfUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="open-pdf-button"
            :aria-label="`Open ${viewerTitle} in a new tab`"
          >
            Open PDF
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.translated-documents-inner {
  max-width: 1430px;
  margin: 0 auto;
  padding: 0 16px 32px;
}

/* Opaque strip behind the site header. Sits under both the header and the
   quick exit bar, but above page content, so nothing shows through the gaps
   either side of the inset header bar. */
.header-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 8998;
  background: rgb(var(--background, 248, 246, 252));
  pointer-events: none;
}

/* ==================== QUICK EXIT BAR ==================== */
/* Fixed under the site header (top set inline from the measured header height)
   so it stays visible for the whole page.
 *
 * IGNITE-65: this MUST stay below .onepass-header (z-index 9000), otherwise it
 * paints over the nav dropdowns. The dropdown carries z-index 10000, but the
 * nav wrapping it sets `isolation: isolate`, which opens a new stacking
 * context - so that 10000 only orders the dropdown against its siblings inside
 * the nav, and from the outside the whole menu is capped at the header's 9000.
 * At an equal 9000 this bar won simply by coming later in the DOM. Raising the
 * dropdown cannot fix it; the bar has to sit lower. The mobile header is 9500
 * and was never affected. */
.quick-exit-bar {
  position: fixed;
  left: 0;
  right: 0;
  z-index: 8999;
  background: #ffffff;
  border-bottom: 1px solid #eeeee9;
}

/* Mirrors the page's wrapper chain, .container > .translated-documents-inner,
   so the bar aligns with the page content at every breakpoint. .container is
   responsive (16px padding, 40px from md, capped at 1280px from xl), so the
   geometry is applied rather than copied as fixed numbers - a hardcoded
   max-width drifts out of alignment as soon as the config changes. */
.quick-exit-container {
  @apply container;
}

.quick-exit-inner {
  padding: 12px 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.quick-exit-copy {
  margin: 0;
  font-family: "Montserrat", sans-serif;
  font-size: 16px;
  line-height: 24px;
  color: #333333;
}

.quick-exit-phone {
  color: inherit;
  text-decoration: none;
}

.quick-exit-button {
  flex-shrink: 0;
  display: inline-block;
  padding: 12px 24px;
  border-radius: 9999px;
  font-family: "Montserrat", sans-serif;
  font-size: 16px;
  line-height: 20px;
  font-weight: 700;
  /* CMS-authored page-level <style> tags can leak underlines onto buttons */
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  background-color: rgb(var(--primary, 153, 0, 241));
  color: #ffffff;
}

.quick-exit-button:focus-visible,
.open-pdf-button:focus-visible,
.language-link:focus-visible {
  outline: 3px solid rgb(var(--primary, 153, 0, 241));
  outline-offset: 2px;
}

@media (max-width: 767px) {
  .quick-exit-inner {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .quick-exit-copy {
    font-size: 14px;
    line-height: 20px;
  }

  .quick-exit-button {
    align-self: stretch;
    text-align: center;
  }
}

/* ==================== LANGUAGE CAROUSEL ==================== */
.language-carousel {
  display: flex;
  align-items: center;
  gap: 8px;
  border-top: 1px solid #eeeee9;
  border-bottom: 1px solid #eeeee9;
  padding: 4px 0;
  margin-bottom: 24px;
}

.carousel-arrow {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  background: none;
  color: #333333;
  cursor: pointer;
}

.carousel-arrow:disabled {
  opacity: 0.25;
  cursor: default;
}

.language-list {
  display: flex;
  flex: 1;
  align-items: stretch;
  gap: 24px;
  margin: 0;
  padding: 0;
  list-style: none;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
}

.language-list::-webkit-scrollbar {
  display: none;
}

.language-link {
  display: block;
  padding: 12px 2px;
  border: 0;
  border-bottom: 3px solid transparent;
  background: none;
  cursor: pointer;
  /* CMS-authored page-level <style> tags underline links and buttons, which
     leaked onto every language and competed with the active underline */
  text-decoration: none;
  white-space: nowrap;
  font-family: "Montserrat", sans-serif;
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  color: #333333;
}

.language-link--active {
  font-weight: 700;
  color: rgb(var(--primary, 153, 0, 241));
  border-bottom-color: rgb(var(--primary, 153, 0, 241));
}

/* ==================== DOCUMENT / VIEWER ==================== */
.viewer-container {
  position: relative;
  max-width: 900px;
  background-color: #222222;
  border-radius: 0.75rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

.viewer-frame {
  display: block;
  width: 100%;
  height: 85vh;
  min-height: 500px;
  border: 0;
  background-color: #222222;
}

.viewer-footer {
  padding: 12px 16px;
  background-color: #222222;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
}

.viewer-download-link {
  color: #ffffff;
  font-weight: 700;
  text-decoration: underline;
}

.viewer-download-link:hover {
  text-decoration: none;
}

.viewer-ios-fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  height: 70vh;
  min-height: 360px;
  padding: 24px;
  text-align: center;
  color: #ffffff;
}

.open-pdf-button {
  display: inline-block;
  padding: 12px 24px;
  border-radius: 9999px;
  font-weight: 700;
  text-decoration: none;
  background-color: rgb(var(--primary, 153, 0, 241));
  color: #ffffff;
}

@media (max-width: 767px) {
  .viewer-frame {
    height: 70vh;
    min-height: 360px;
  }
}
</style>
